/**
 * Admin Pricing & Fulfilment — /admin/settings/pricing-and-fulfilment
 *
 * Feb 2026 — Consolidated dashboard for all commercial + fulfilment
 * controls that affect checkout pricing, Stripe totals, and how a
 * booking is fulfilled (mail-out, lab collection, partner-supplied,
 * customer drop-off). Sits under Admin → Settings → Pricing &
 * Fulfilment.
 *
 * Sections:
 *   1. Package Pricing               ← /api/admin/all-inclusive-pricing
 *   2. Mail-Out Kit Settings         ← link to /admin/settings/posted-kit
 *   3. Local Lab Collection & Drop-Off ← /api/admin/local-lab-config
 *   4. Partner-Supplied Kit Credits  ← link to /admin/settings/bulk-kit
 *   5. Fulfilment Rules              ← per-partner toggles (read-only summary)
 *   6. Audit History                 ← /api/admin/local-lab-config/audit
 *
 * The local-lab flag ships DISABLED and must be flipped ON from this
 * page after preview review. Every save on the local-lab config
 * writes an audit row so the trail is fully reconstructable.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  DollarSign,
  MapPin,
  Package,
  Users,
  Truck,
  History,
  Save,
  RefreshCw,
  Loader2,
  Info,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";


// ─────────────────────────────────────────────────────────────
// Small primitives
// ─────────────────────────────────────────────────────────────
function SectionCard({ icon: Icon, title, subtitle, action, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
    >
      <header className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-start gap-3 min-w-0">
          <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{subtitle}</p>}
          </div>
        </div>
        {action}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function LabelledInput({ label, value, onChange, type = "text", placeholder, hint, testId, disabled }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 disabled:bg-slate-50 disabled:text-slate-500 tabular-nums"
      />
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </label>
  );
}

function Toggle({ checked, onChange, testId, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-3 select-none`}
    >
      <span
        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
          checked ? "bg-cyan-600" : "bg-slate-300"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </span>
      <span className="text-sm font-semibold text-slate-900">{label}</span>
    </button>
  );
}


// ─────────────────────────────────────────────────────────────
// Package Pricing
// ─────────────────────────────────────────────────────────────
function PackagePricingSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/all-inclusive-pricing");
      setPricing(data);
      const nextDrafts = {};
      for (const [k, v] of Object.entries(data?.prices || {})) {
        nextDrafts[k] = ((v?.cents ?? 0) / 100).toFixed(2);
      }
      setDrafts(nextDrafts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (testId) => {
    const dollars = Number(drafts[testId]);
    if (!Number.isFinite(dollars) || dollars <= 0) {
      toast.error("Price must be a positive number");
      return;
    }
    setSaving(testId);
    try {
      await api.put(`/admin/all-inclusive-pricing/${testId}`, {
        cents: Math.round(dollars * 100),
      });
      toast.success(`Updated price for ${testId}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading pricing…</div>;
  }
  const rows = Object.entries(pricing?.prices || {});
  return (
    <div>
      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
        Server-authoritative all-inclusive prices. Applied on every checkout when the customer&apos;s
        booking uses <code className="px-1 rounded bg-slate-100 font-mono text-[10px]">pricing_model = all_inclusive_v1</code>.
        No separate $34 postage fee — postage is baked in for mail-outs.
      </p>
      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="text-sm text-slate-500 italic">No prices configured yet.</div>
        )}
        {rows.map(([testId, row]) => (
          <div key={testId} data-testid={`pricing-row-${testId}`} className="flex flex-wrap items-end gap-3">
            <div className="min-w-[160px]">
              <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">Test ID</div>
              <div className="text-sm font-mono text-slate-900 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200">{testId}</div>
            </div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">Price (AUD)</div>
              <div className="relative">
                <span className="absolute left-3 top-2.5 text-sm text-slate-500">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid={`pricing-input-${testId}`}
                  value={drafts[testId] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [testId]: e.target.value }))}
                  className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 tabular-nums"
                />
              </div>
            </div>
            <Button
              size="sm"
              data-testid={`pricing-save-${testId}`}
              onClick={() => save(testId)}
              disabled={saving === testId}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {saving === testId ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Save className="w-3.5 h-3.5 mr-1.5"/>}
              Save
            </Button>
          </div>
        ))}
      </div>
      {pricing?.effective_from && (
        <div className="mt-4 text-[11px] text-slate-500">
          Effective from: <span className="font-mono">{pricing.effective_from}</span>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Local Lab Collection & Drop-Off
// ─────────────────────────────────────────────────────────────
function LocalLabSection({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [postcodesText, setPostcodesText] = useState("");
  const [discountDollars, setDiscountDollars] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [lab, setLab] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/local-lab-config");
      setConfig(data);
      setEnabled(!!data.enabled);
      setPostcodesText((data.eligible_postcodes || []).join(", "));
      setDiscountDollars(((data.discount_amount_cents ?? 2000) / 100).toFixed(2));
      setLab(data.lab_location || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load local-lab config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/admin/local-lab-config", patch);
      toast.success("Local-lab config saved");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const commitAll = async () => {
    const postcodes = postcodesText
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const dollars = Number(discountDollars);
    if (!Number.isFinite(dollars) || dollars < 0) {
      toast.error("Discount must be a non-negative number");
      return;
    }
    await save({
      enabled,
      discount_amount_cents: Math.round(dollars * 100),
      eligible_postcodes: postcodes,
      lab_location: lab,
    });
  };

  if (loading) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading local-lab config…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-200 bg-slate-50/50">
        <div className="min-w-0">
          <Toggle
            checked={enabled}
            onChange={setEnabled}
            testId="local-lab-enabled-toggle"
            label={enabled ? "Feature ENABLED" : "Feature DISABLED"}
          />
          <p className="mt-2 text-xs text-slate-500 leading-relaxed">
            When ON, the booking form shows Lab Pickup and Bring-Your-Own-Bottle options for eligible
            postcodes with a <strong>$20 saving</strong>. When OFF, only mail-out (+ partner-supplied) surfaces.
          </p>
        </div>
        {config?.updated_by && (
          <div className="text-right text-[11px] text-slate-500 shrink-0">
            <div>Last updated</div>
            <div className="font-mono text-slate-700">{config.updated_by}</div>
            <div className="font-mono text-slate-500">{(config.updated_at || "").replace("T", " ").slice(0, 19)}</div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <LabelledInput
          label="Discount (AUD)"
          type="number"
          value={discountDollars}
          onChange={setDiscountDollars}
          hint="Applied per booking when the customer picks a lab option on an eligible postcode."
          testId="local-lab-discount-input"
        />
        <LabelledInput
          label="Eligible postcodes (comma-separated)"
          value={postcodesText}
          onChange={setPostcodesText}
          hint={`Current: ${(config?.eligible_postcodes || []).length} postcode(s). Feb 2026 pilot: 4566, 4562, 4563, 4558.`}
          testId="local-lab-postcodes-input"
        />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">Lab collection location</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <LabelledInput label="Lab name"    value={lab.name}     onChange={(v) => setLab((x) => ({ ...x, name: v }))}     testId="local-lab-location-name" />
          <LabelledInput label="Address line" value={lab.line1}    onChange={(v) => setLab((x) => ({ ...x, line1: v }))}    testId="local-lab-location-line1" />
          <LabelledInput label="Suburb"       value={lab.suburb}   onChange={(v) => setLab((x) => ({ ...x, suburb: v }))}   testId="local-lab-location-suburb" />
          <LabelledInput label="State"        value={lab.state}    onChange={(v) => setLab((x) => ({ ...x, state: v }))}    testId="local-lab-location-state" />
          <LabelledInput label="Postcode"     value={lab.postcode} onChange={(v) => setLab((x) => ({ ...x, postcode: v }))} testId="local-lab-location-postcode" />
          <LabelledInput label="Hours"        value={lab.hours}    onChange={(v) => setLab((x) => ({ ...x, hours: v }))}    testId="local-lab-location-hours" />
        </div>
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-1">Instructions (shown to customer)</div>
          <textarea
            data-testid="local-lab-location-instructions"
            value={lab.instructions || ""}
            onChange={(e) => setLab((x) => ({ ...x, instructions: e.target.value }))}
            rows={2}
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          size="sm"
          variant="outline"
          data-testid="local-lab-reload-btn"
          onClick={load}
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5"/> Reload
        </Button>
        <Button
          size="sm"
          data-testid="local-lab-save-btn"
          onClick={commitAll}
          disabled={saving}
          className="bg-cyan-600 hover:bg-cyan-700 text-white"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5"/> : <Save className="w-3.5 h-3.5 mr-1.5"/>}
          Save all changes
        </Button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Audit History
// ─────────────────────────────────────────────────────────────
function AuditSection({ refreshKey }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/local-lab-config/audit?limit=50");
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit history");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]); // reload when parent signals

  if (loading) {
    return <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading audit history…</div>;
  }
  if (!rows.length) {
    return <div className="text-sm text-slate-500 italic">No audit rows yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 uppercase tracking-[0.14em] text-[10px] font-bold">
            <th className="text-left py-2 pr-3">When</th>
            <th className="text-left py-2 pr-3">Who</th>
            <th className="text-left py-2 pr-3">Change</th>
          </tr>
        </thead>
        <tbody data-testid="local-lab-audit-tbody">
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-slate-100 align-top">
              <td className="py-2 pr-3 font-mono text-slate-500 whitespace-nowrap">{(r.at || "").replace("T", " ").slice(0, 19)}</td>
              <td className="py-2 pr-3 text-slate-700 whitespace-nowrap">{r.actor || r.actor_email || "—"}</td>
              <td className="py-2 pr-3 text-slate-700 leading-relaxed">
                <pre className="whitespace-pre-wrap font-mono text-[11px] text-slate-600">{JSON.stringify(r.patch || r.after || {}, null, 2)}</pre>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Page shell
// ─────────────────────────────────────────────────────────────
export default function AdminPricingAndFulfilment() {
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpAudit = () => setRefreshKey((x) => x + 1);

  return (
    <PortalShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs">
            <Link to="/admin/settings" className="text-cyan-700 hover:underline">Admin</Link>
            <span className="text-slate-400">/</span>
            <Link to="/admin/settings" className="text-cyan-700 hover:underline">Settings</Link>
            <span className="text-slate-400">/</span>
            <span className="text-slate-600">Pricing &amp; Fulfilment</span>
          </div>
          <h1
            data-testid="pricing-and-fulfilment-heading"
            className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2"
          >
            Pricing &amp; Fulfilment
          </h1>
          <p className="mt-2 text-sm text-slate-500 leading-relaxed max-w-3xl">
            All commercial + fulfilment controls that affect checkout pricing, Stripe totals and how a booking is fulfilled.
            Applies to every AWARE booking, not just PM Program bookings.
          </p>
        </div>

        <div className="space-y-6">
          {/* 1. Package Pricing */}
          <SectionCard
            testId="section-package-pricing"
            icon={DollarSign}
            title="Package Pricing"
            subtitle="All-inclusive prices per test. Server re-computes checkout totals from these values — never trust the browser."
          >
            <PackagePricingSection />
          </SectionCard>

          {/* 2. Mail-Out Kit Settings — link to existing screen */}
          <SectionCard
            testId="section-mail-out-kit"
            icon={Package}
            title="Mail-Out Kit Settings"
            subtitle="Kit example image, posted-kit fee (kept at $0 under all-inclusive pricing), lab pickup address, Starshipit parcel dimensions."
            action={
              <Link
                to="/admin/settings/posted-kit"
                data-testid="link-posted-kit-settings"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800 hover:underline whitespace-nowrap"
              >
                Open settings <ExternalLink className="w-3.5 h-3.5"/>
              </Link>
            }
          >
            <p className="text-xs text-slate-500 leading-relaxed">
              Under the all-inclusive pricing model, postage is baked into the package price — the mail-out fee should
              stay at <strong>$0</strong>. The dedicated Mail-Out Kit Settings page still manages the kit example image,
              caption, lab pickup address and Starshipit parcel dimensions.
            </p>
          </SectionCard>

          {/* 3. Local Lab Collection & Drop-Off */}
          <SectionCard
            testId="section-local-lab"
            icon={MapPin}
            title="Local Lab Collection & Drop-Off"
            subtitle="Feature flag, eligible postcodes, $20 local-lab saving, lab collection location shown to eligible customers."
          >
            <LocalLabSection onSaved={bumpAudit} />
          </SectionCard>

          {/* 4. Partner-Supplied Kit Credits */}
          <SectionCard
            testId="section-partner-kits"
            icon={Users}
            title="Partner-Supplied Kit Credits"
            subtitle="Customer charge for partner-supplied kits, partner reimbursement amount, AWARE distribution fee."
            action={
              <Link
                to="/admin/settings/bulk-kit"
                data-testid="link-bulk-kit-settings"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-700 hover:text-cyan-800 hover:underline whitespace-nowrap"
              >
                Open settings <ExternalLink className="w-3.5 h-3.5"/>
              </Link>
            }
          >
            <p className="text-xs text-slate-500 leading-relaxed">
              Bulk Kit Settings governs the customer-facing partner-supplied kit charge and the partner-reimbursement
              side. Same page also controls the per-box parcel dimensions Starshipit uses for outbound partner
              orders.
            </p>
          </SectionCard>

          {/* 5. Fulfilment Rules */}
          <SectionCard
            testId="section-fulfilment-rules"
            icon={Truck}
            title="Fulfilment Rules"
            subtitle="Per-partner toggles decide which fulfilment options a partner-attributed booking sees on the customer form."
          >
            <div className="text-xs text-slate-600 leading-relaxed space-y-2">
              <p>
                Every partner-attributed booking sees the fulfilment options gated by their partner&apos;s toggles
                (<code className="px-1 rounded bg-slate-100 font-mono text-[10px]">allow_lab_pickup</code>,
                <code className="px-1 rounded bg-slate-100 font-mono text-[10px]">allow_customer_dropoff</code>,
                <code className="px-1 rounded bg-slate-100 font-mono text-[10px]">allow_partner_supplied_kit</code>).
                Public (non-attributed) bookings see the full set gated only by the Local Lab feature flag +
                postcode eligibility above.
              </p>
              <p>
                Edit these per-partner from the <Link className="text-cyan-700 hover:underline" to="/admin/pws-partners">PWS Partners</Link> page.
              </p>
            </div>
          </SectionCard>

          {/* 6. Audit History */}
          <SectionCard
            testId="section-audit"
            icon={History}
            title="Audit History"
            subtitle="Every change to the local-lab config is written to an audit row. The 50 most recent rows are shown below."
          >
            <AuditSection refreshKey={refreshKey} />
          </SectionCard>
        </div>
      </div>
    </PortalShell>
  );
}
